import { Col, Row } from 'antd';
import { useEffect, useRef, useState } from 'react';

import AlertBar from '../../components/AlertBar';
import HeaderCard from '../../components/HeaderCard';
import SearchBox from '../../components/SearchBox/index';
import Select from '../../components/Select/';
import FarmersTable from './components/farmer-table/index';

import useFarmersQuery from './hooks/useFarmersQuery';

import { CONTRACT_STATUS } from '../../utils/constants';

import MultiSelect from 'components/MultiSelect';
import useAgronomistList from 'hooks/useAgronomistList';
import { useSearchParams } from 'react-router-dom';
import useFarmerGroup from 'screens/farmer-groups/hooks/useFarmerGroupListQuery';
import { populateOptions } from 'utils/helpers';
import TopFilters from './components/TopFilters';
import useFarmerFilters from './hooks/useFarmerFilters';

function Farmers() {
  const [searchFarmerGroup, setSearchFarmerGroup] = useState<string>();
  const [searchParams] = useSearchParams();
  const groupIdParams = searchParams.get('groupId');
  const groupNameParams = searchParams.get('groupName');
  const agronomistParams = searchParams.get('agronomist');
  const {
    agronomist,
    country,
    groupName,
    contractStatus,
    farmerStatus,
    setAgronomist,
    setCountry,
    setGroupName,
    setContractStatus,
    setFarmerStatus,
    setSearchedName,
    filterParams,
  } = useFarmerFilters();

  const searchRef = useRef<string | null>(null);

  const {
    data: farmersData,
    error,
    isLoading,
    total,
    handlePageChange,
    setCurrentPage,
    currentPage,
    pageSize,
  } = useFarmersQuery(filterParams);

  const { farmerGroupOptions, isLoading: isFarmerGroupLoading } =
    useFarmerGroup({
      size: 20,
      search: searchFarmerGroup,
      country: country,
    });

  const prefilledGroupId = groupIdParams
    ? { value: groupIdParams, label: groupNameParams }
    : undefined;

  const populatedFarmerGroupOptions = populateOptions(
    prefilledGroupId,
    farmerGroupOptions,
    isFarmerGroupLoading,
  );

  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country);

  useEffect(() => {
    if (agronomistParams != null) {
      setAgronomist([agronomistParams]);
    }
  }, [agronomistParams]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value === '') setSearchedName(null);
    searchRef.current = value;
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchedName(searchRef?.current);
  };

  useEffect(() => {
    handlePageChange(1, pageSize);
  }, [filterParams, handlePageChange, pageSize]);

  const tableTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tableTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    if (groupIdParams) setGroupName([groupIdParams]);
  }, []);

  return (
    <div className="dashboard-container" ref={tableTopRef}>
      {error?.message && <AlertBar type="error" message={error?.message} />}
      <HeaderCard
        headerLabel="Farmers Management"
        headerInfo="Farmers overview appears here"
        filters={
          <TopFilters
            country={country}
            onCountryChange={setCountry}
            farmerStatus={farmerStatus}
            onFarmerStatusChange={setFarmerStatus}
          />
        }
      />

      <Row>
        <Col span={24} className="table-container">
          <Row className="filters">
            <SearchBox
              placeholder="Search farmer name here"
              onSearch={handleSearch}
              onChange={handleSearchChange}
            />
            <MultiSelect
              label="Group Name"
              allowClear
              showSearch
              onSearch={(value) => setSearchFarmerGroup(value)}
              placeholder="Select Group Name"
              loading={isFarmerGroupLoading}
              options={populatedFarmerGroupOptions}
              value={groupName}
              onChange={setGroupName}
            />
            <MultiSelect
              label="Agronomist"
              placeholder="Select Agronomist"
              options={[
                { value: '-', label: 'Not Assigned Yet' },
                ...(agronomistFilterOptions?.length
                  ? agronomistFilterOptions
                  : []),
              ]}
              value={agronomist}
              onChange={setAgronomist}
              disabled={isAgronomistLoading}
              allowClear
            />
            <Select
              label="Contract Status"
              placeholder="Select Contract Status"
              options={CONTRACT_STATUS}
              value={contractStatus}
              onChange={setContractStatus}
              allowClear
            />
          </Row>
          <FarmersTable
            data={farmersData}
            isLoading={isLoading}
            pagination={{
              currentPage,
              pageSize,
              total,
              handlePageChange,
            }}
          />
        </Col>
      </Row>
    </div>
  );
}

export default Farmers;
